import { Trans, useLingui } from '@lingui/react'
import { useState } from 'react'
import { useForm } from 'react-hook-form/dist/react-hook-form.ie11'

import { GENERAL_ERROR } from 'shared/consts/errorCodes'
import {
  BIRTHDAY_DAY_FIELD,
  BIRTHDAY_MONTH_FIELD,
  BIRTHDAY_YEAR_FIELD,
  COMPANY_NAME_FIELD,
  EMAIL_FIELD,
  FIRSTNAME_FIELD,
  LASTNAME_FIELD,
  LOGIN_REGISTER_ACTION_FIELD,
  NEWSLETTER_ACCEPTED_FIELD,
  PASSWORD_FIELD,
  REGISTER_CUSTOMER_TYPE_PROFESSIONAL_FIELD_VALUE,
  REGISTER_FORM_ACTION_FIELD_VALUE,
  REGISTER_FORM_CUSTOMER_TYPE_FIELD,
  TARGET_ANCHOR_FIELD,
  TARGET_PAGE_CODE_FIELD,
  TARGET_PRODUCT_CODE_FIELD,
  TOS_ACCEPTED_FIELD,
  VAT_NUMBER_EXEMPTION_FIELD,
  VAT_NUMBER_FIELD,
} from 'shared/consts/formFields'
import Button from 'views/components/atoms/Button'
import Form from 'views/components/atoms/Form'
import Input from 'views/components/atoms/Input'
import InputCheckbox from 'views/components/molecules/InputCheckbox'
import InputCheckboxOrRadio from 'views/components/molecules/InputCheckboxOrRadio'
import InputTextWithFloatingLabel from 'views/components/molecules/InputTextWithFloatingLabel'
import InputWithUnEditablePrefill from 'views/components/molecules/InputWithUnEditablePrefill'
import BirthDaySelect from 'views/components/organisms/BirthDaySelect'
import ErrorNotifications from 'views/components/organisms/ErrorNotifications'
import {
  errorMessagesFieldMap,
  MAX_NAME_LENGTH,
  MAX_PASSWORD_LENGTH,
  MIN_AGE,
  MIN_COMPANY_NAME_LENGTH,
  MIN_PASSWORD_LENGTH,
  translateErrors,
} from 'views/components/organisms/RegisterForm/consts'
import Salutation from 'views/components/organisms/Salutation'
import LoadingIndicatorButton from 'views/components/organisms/SellerRating/LoadingIndicatorButton'
import useMountPoints from 'views/enhancers/useMountPoints'
import withMountPoints from 'views/enhancers/withMountPoints'
import { mountPointsType } from 'views/utils/propTypes'
import {
  usePageProperties,
  usePublicConfig,
} from 'views/utils/shopAndRuntimeConfigUtils'
import './ProfessionalAccounrRegisterForm.scss'

const ProfessionalAccountRegisterForm = () => {
  const { i18n } = useLingui()
  const FIRSTNAME_ID = 'professionalAccountFirstName'
  const LASTNAME_ID = 'professionalAccountLastName'
  const EMAIL_ID = 'professionalAccountEmail'
  const PASSWORD_ID = 'professionalAccountPassword'
  const NEWSLETTER_ACCEPTED_FIELD_ID = 'professionalAccountNewsletterAccepted'
  const TOS_ACCEPTED_FIELD_ID = 'professionalAccountTosAccepted'
  const VAT_NUMBER_EXEMPTION_REQUIRED_FIELD_ID =
    'vatNumberExemptionRequiredField'
  const VAT_NUMBER_EXEMPTION_NOT_REQUIRED_FIELD_ID =
    'vatNumberExemptionNotRequiredField'

  const mountPoints = useMountPoints()

  const { isFriendlyCaptchaToggleActive } = usePageProperties()

  const language = usePublicConfig().language

  const errors = usePageProperties().registerErrors
  const defaultValues = usePageProperties().registerValues
  const { targetPageCode, targetProductCode, targetAnchor } =
    usePageProperties().targetPageParams || {}

  const { watch } = useForm({
    defaultValues,
  })

  const fieldErrors = {}
  const globalErrors = []

  const translatedErrors = translateErrors(mountPoints)

  errors?.forEach(error => {
    const fieldName = errorMessagesFieldMap[error]

    if (fieldName) {
      fieldErrors[fieldName] = {
        message: translatedErrors[error] ?? translatedErrors[GENERAL_ERROR],
      }
      return
    }

    globalErrors.push(
      translatedErrors[error] ?? translatedErrors[GENERAL_ERROR]
    )
  })

  const [birthdayDay, setBirthdayDay] = useState('')
  const [birthdayMonth, setBirthdayMonth] = useState('')
  const [birthdayYear, setBirthdayYear] = useState('')

  const onBirthdayDayChange = value => {
    setBirthdayDay(value)
  }

  const onBirthdayMonthChange = value => {
    setBirthdayMonth(value)
  }

  const onBirthdayYearChange = value => {
    setBirthdayYear(value)
  }

  const [isLoading, setIsLoading] = useState(false)
  const [vatExemptionNeeded, setVatExemptionNeeded] = useState(true)

  const handleOnChange = vatExemptionNeeded => {
    setVatExemptionNeeded(vatExemptionNeeded)
    if (vatExemptionNeeded) {
      document
        .getElementById('vatNumberInputItem')
        ?.classList.remove('o-ProfessionalRegisterForm__taxInput__display_none')
      document
        .getElementById('vatNumberInputItem')
        ?.classList.add('o-ProfessionalRegisterForm__taxInput__display_inline')
      document.getElementById(VAT_NUMBER_FIELD)?.removeAttribute('disabled')
    } else {
      document
        .getElementById('vatNumberInputItem')
        ?.classList.remove(
          'o-ProfessionalRegisterForm__taxInput__display_inline'
        )
      document
        .getElementById('vatNumberInputItem')
        ?.classList.add('o-ProfessionalRegisterForm__taxInput__display_none')
      document.getElementById(VAT_NUMBER_FIELD)?.setAttribute('disabled', '')
    }
  }

  const onSubmit = e => {
    const isFormValid = e.target.checkValidity()
    if (isFormValid) {
      setIsLoading(true)
    }
  }

  return (
    <>
      <ErrorNotifications errors={globalErrors} />
      <Form
        id="professional-account-register-form"
        name="professional-account-register-form"
        data-clientside-hook="formValidation"
        action={mountPoints.getProfessionalAccountRegister()}
        method="POST"
        onSubmit={onSubmit}
      >
        <Salutation type="professional-account" required watch={watch} />
        <ul className="l-grid l-grid--tight o-RegisterForm__input-list">
          <li className="l-grid__item l-col--6-of-12 u-margin--bottom o-RegisterForm__input-wrapper">
            <InputTextWithFloatingLabel
              name={FIRSTNAME_FIELD}
              id={FIRSTNAME_ID}
              errors={fieldErrors}
              inputType="text"
              required
              defaultValue={watch(FIRSTNAME_FIELD)}
              maxLength={MAX_NAME_LENGTH}
            >
              <Trans id="general.label.firstName" />
            </InputTextWithFloatingLabel>
          </li>
          <li className="l-grid__item l-col--6-of-12 u-margin--bottom o-RegisterForm__input-wrapper o-RegisterForm__input-wrapper--lastname">
            <InputTextWithFloatingLabel
              name={LASTNAME_FIELD}
              id={LASTNAME_ID}
              errors={fieldErrors}
              inputType="text"
              required
              defaultValue={watch(LASTNAME_FIELD)}
              maxLength={MAX_NAME_LENGTH}
            >
              <Trans id="general.label.surname" />
            </InputTextWithFloatingLabel>
          </li>
        </ul>
        <ul className="a-list-reset u-margin-large--bottom o-RegisterForm__input-list">
          <li className="u-margin--bottom o-RegisterForm__input-wrapper">
            <InputTextWithFloatingLabel
              name={COMPANY_NAME_FIELD}
              id={COMPANY_NAME_FIELD}
              errors={fieldErrors}
              inputType="text"
              required
              defaultValue={watch(COMPANY_NAME_FIELD)}
              minLength={MIN_COMPANY_NAME_LENGTH}
            >
              <Trans id="general.label.companyName" />
            </InputTextWithFloatingLabel>
          </li>
        </ul>
        <ul className="a-list-reset u-margin-large--bottom o-RegisterForm__input-list">
          <li className="u-margin--bottom o-RegisterForm__input-wrapper">
            <div className="l-flex">
              <div>
                <InputCheckboxOrRadio
                  name={VAT_NUMBER_EXEMPTION_FIELD}
                  id={VAT_NUMBER_EXEMPTION_REQUIRED_FIELD_ID}
                  value="true"
                  label="VAT"
                  type="radio"
                  onClick={() => handleOnChange(true)}
                  checked={vatExemptionNeeded}
                >
                  <Trans id="general.label.vatExemptionNeeded" />
                </InputCheckboxOrRadio>
              </div>
              <div className="u-padding--left@medium">
                <InputCheckboxOrRadio
                  name={VAT_NUMBER_EXEMPTION_FIELD}
                  id={VAT_NUMBER_EXEMPTION_NOT_REQUIRED_FIELD_ID}
                  value="false"
                  label="No VAT"
                  type="radio"
                  onClick={() => handleOnChange(false)}
                  checked={!vatExemptionNeeded}
                >
                  <Trans id="general.label.vatExemptionNotNeeded" />
                </InputCheckboxOrRadio>
              </div>
            </div>
          </li>
        </ul>
        <ul className="a-list-reset u-margin-large--bottom o-RegisterForm__input-list">
          <li
            id="vatNumberInputItem"
            className="u-margin--bottom o-RegisterForm__input-wrapper"
          >
            <InputWithUnEditablePrefill
              name={VAT_NUMBER_FIELD}
              id={VAT_NUMBER_FIELD}
              errors={fieldErrors}
              inputType="text"
              required
              preFillText="BE"
              defaultValue={watch(VAT_NUMBER_FIELD)}
            >
              <Trans id="general.label.vatNumber" />
            </InputWithUnEditablePrefill>
          </li>
        </ul>
        <ul className="a-list-reset u-margin-large--bottom o-RegisterForm__input-list">
          <li className="u-margin--bottom o-RegisterForm__input-wrapper--email">
            <InputTextWithFloatingLabel
              name={EMAIL_FIELD}
              id={EMAIL_ID}
              errors={fieldErrors}
              inputType="email"
              defaultValue={watch(EMAIL_FIELD)}
              required
            >
              <Trans id="general.label.email" />
            </InputTextWithFloatingLabel>
          </li>
          <li className="u-margin--bottom o-RegisterForm__input-wrapper o-RegisterForm__input-wrapper--password">
            <InputTextWithFloatingLabel
              name={PASSWORD_FIELD}
              id={PASSWORD_ID}
              errors={fieldErrors}
              inputType="password"
              required
              minLength={MIN_PASSWORD_LENGTH}
              maxLength={MAX_PASSWORD_LENGTH}
              defaultValue={null}
              isNotEqualTo={{
                fieldId: 'registerForm-eMail',
                errorMessage: i18n._('registration.setPassword.emailError'),
              }}
            >
              <Trans id="general.label.password" />{' '}
              <Trans id="general.label.passwordRequirements" />
            </InputTextWithFloatingLabel>
          </li>
        </ul>

        <div className="u-margin--bottom o-RegisterForm__BirthDaySelect">
          <div className="u-margin-xx-small--bottom o-RegisterForm__BirthDaySelect__label-wrapper">
            <span className="a-label o-RegisterForm__BirthDaySelect__label">
              <Trans id="general.label.birthday" />
            </span>
            <span className="a-label">*</span>
          </div>
          <BirthDaySelect
            type="professional-account"
            minAge={MIN_AGE}
            daySelectName={BIRTHDAY_DAY_FIELD}
            monthSelectName={BIRTHDAY_MONTH_FIELD}
            yearSelectName={BIRTHDAY_YEAR_FIELD}
            selectedDay={parseInt(birthdayDay, 10)}
            selectedMonth={parseInt(birthdayMonth, 10)}
            selectedYear={parseInt(birthdayYear, 10)}
            onSelectedDayChange={onBirthdayDayChange}
            onSelectedMonthChange={onBirthdayMonthChange}
            onSelectedYearChange={onBirthdayYearChange}
          />
        </div>

        <ul className="a-list-reset">
          <li className="l-flex u-margin--bottom o-RegisterForm__checkbox-wrapper">
            <InputCheckbox
              id={NEWSLETTER_ACCEPTED_FIELD_ID}
              name={NEWSLETTER_ACCEPTED_FIELD}
              value="true"
              labelSize="small"
            >
              <Trans
                id="registrationForm.label.check.newsletter"
                // eslint-disable-next-line jsx-a11y/anchor-has-content
                components={{ 0: <a href={mountPoints.getNewsletter()} /> }}
              />
            </InputCheckbox>
          </li>
          <li className="l-flex u-margin--bottom o-RegisterForm__checkbox-wrapper">
            <InputCheckbox
              id={TOS_ACCEPTED_FIELD_ID}
              name={TOS_ACCEPTED_FIELD}
              value="true"
              labelSize="small"
              required
            >
              <Trans
                id="registrationForm.label.check.toc"
                components={{
                  /* eslint-disable jsx-a11y/anchor-has-content */
                  0: <a href={mountPoints.getAGB()} />,
                  1: <a href={mountPoints.getPrivacySecurity()} />,
                  2: <a href={mountPoints.getPrivacySecurityCreditCheck()} />,
                  /* eslint-enable jsx-a11y/anchor-has-content */
                }}
              />
            </InputCheckbox>
          </li>
          <li className="l-flex u-margin--bottom">
            {isFriendlyCaptchaToggleActive && (
              <div
                className="frc-captcha"
                id="professional-account-registerForm-frc-captcha"
                data-sitekey="FCMUL52LQ1I634TN"
                data-lang={language}
              />
            )}
          </li>
          <li className="u-margin-large--bottom o-RegisterForm__buttons">
            <div className="l-flex--center l-flex@medium l-flex__row_reverse@medium o-RegisterForm__buttons-wrapper">
              <div className="u-margin--bottom u-no-margin--bottom@medium l-flex__primary@medium o-RegisterForm__submit">
                <LoadingIndicatorButton
                  id="professional-account-register-submit-btn"
                  name="submit-button"
                  type="submit"
                  loading={isLoading}
                  disabled={isFriendlyCaptchaToggleActive}
                  data-loading-label={i18n._('button.loading.label')}
                  data-submit-styles="standard"
                >
                  <Trans id="general.button.label.createAccount" />
                </LoadingIndicatorButton>
              </div>
              <div className="l-flex__secondary@medium u-margin--right@medium o-RegisterForm__back">
                <Button
                  style="buttonGhost"
                  name="back-button"
                  isClientsideControlled
                  data-a11y-dialog-hide
                >
                  <Trans id="general.link.label.back" />
                </Button>
              </div>
            </div>
          </li>
          <li>
            <p className="u-color--grey a-h4-tiny u-padding-large--bottom o-RegisterForm__mandatory-field-indicator">
              <Trans id="general.form.label.mandatoryFieldIndicator" />
            </p>
          </li>
        </ul>
        {targetPageCode && (
          <Input
            type="hidden"
            name={TARGET_PAGE_CODE_FIELD}
            value={targetPageCode}
          />
        )}
        {targetProductCode && (
          <Input
            type="hidden"
            name={TARGET_PRODUCT_CODE_FIELD}
            value={targetProductCode}
          />
        )}
        {targetAnchor && (
          <Input
            type="hidden"
            name={TARGET_ANCHOR_FIELD}
            value={targetAnchor}
          />
        )}
        <input
          type="hidden"
          name={LOGIN_REGISTER_ACTION_FIELD}
          value={REGISTER_FORM_ACTION_FIELD_VALUE}
        />
        <input
          type="hidden"
          name={REGISTER_FORM_CUSTOMER_TYPE_FIELD}
          value={REGISTER_CUSTOMER_TYPE_PROFESSIONAL_FIELD_VALUE}
        />
      </Form>
    </>
  )
}

ProfessionalAccountRegisterForm.propTypes = {
  mountPoints: mountPointsType.isRequired,
}

ProfessionalAccountRegisterForm.defaultProps = {}

export default withMountPoints(ProfessionalAccountRegisterForm)
